import buildProductVariantLinkTitle from '../../../helpers/products/buildProductVariantLinkTitle';
import { matchesChannelId } from '../../products/helpers/channelHelpers';
import { findListingEntity } from '../../products/helpers/entityReadHelpers';

const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;

const getListingVariantEntity = (productsState, listingVariantId) => (
  productsState?.entities?.listingVariants?.[listingVariantId] ?? null
);

const getMasterVariant = (masterProduct, masterVariantId) => (
  (masterProduct?.variants || []).find(
    ({ _id }) => String(_id) === String(masterVariantId),
  ) ?? null
);

/**
 * Canonical asset listing-link channel fields.
 * salesChannelId = channel identity; salesChannel = channel type only.
 */
export const resolveLinkChannelFields = ({
  channelId,
  listingProduct,
  salesChannels = [],
} = {}) => {
  const salesChannelId = channelId || listingProduct?.salesChannelId || null;
  const idStr = salesChannelId != null ? String(salesChannelId) : null;

  let salesChannel = listingProduct?.salesChannel || null;
  if (salesChannel && idStr && String(salesChannel) === idStr) {
    salesChannel = null;
  }

  if (!salesChannel && idStr) {
    const byId = salesChannels.find((channel) => String(channel._id) === idStr);
    if (byId?.type) {
      salesChannel = byId.type;
    }
  }

  if (!salesChannel && idStr && !OBJECT_ID_RE.test(idStr)) {
    salesChannel = idStr;
  }

  return {
    ...(idStr ? { salesChannelId: idStr } : {}),
    ...(salesChannel ? { salesChannel } : {}),
  };
};

export const buildMasterVariantLink = (masterProduct, variantId) => {
  const variant = (masterProduct?.variants || []).find(
    ({ _id }) => String(_id) === String(variantId),
  );

  if (!variant) return null;

  const variantTitle = variant.title || variant.displayName || variant._id;

  return {
    variantId: variant._id,
    productId: masterProduct._id,
    title: buildProductVariantLinkTitle(masterProduct.title, variantTitle),
    type: 'masterVariant',
  };
};

export const buildListingGalleryLink = ({
  listingId,
  listingProduct,
  masterProductId,
  salesChannel,
  salesChannelId,
  isDraft = true,
}) => {
  if (!listingId || !listingProduct) return null;

  return {
    _id: listingId,
    title: listingProduct.title || listingId,
    type: 'listing',
    ...(masterProductId ? { masterProductId } : {}),
    ...(salesChannelId ? { salesChannelId } : {}),
    ...(salesChannel ? { salesChannel } : {}),
    ...(isDraft ? { isDraft: true } : {}),
  };
};

export const buildListingVariantLink = ({
  masterProduct,
  productsState,
  channelId,
  listingId,
  listingVariantId,
  isDraft = true,
  salesChannels = [],
  listingProduct,
}) => {
  const channelFields = resolveLinkChannelFields({
    channelId,
    listingProduct,
    salesChannels,
  });
  const listingVariant = getListingVariantEntity(productsState, listingVariantId);

  const productTitle = listingProduct?.title || masterProduct?.title;

  if (listingVariant?.masterVariantId) {
    const masterVariant = getMasterVariant(masterProduct, listingVariant.masterVariantId);
    const variantTitle = listingVariant.title
      || masterVariant?.title
      || masterVariant?.displayName
      || listingVariantId;

    return {
      variantId: listingVariantId,
      productId: listingId,
      title: buildProductVariantLinkTitle(productTitle, variantTitle),
      type: 'listingVariant',
      masterProductId: masterProduct._id,
      masterVariantId: listingVariant.masterVariantId,
      ...channelFields,
      ...(isDraft ? { isDraft: true } : {}),
    };
  }

  const masterVariant = (masterProduct?.variants || []).find((variant) => (
    (variant.channels || []).some(
      (channel) => matchesChannelId(channel, channelId)
        && String(channel._id) === String(listingVariantId),
    )
  ));

  const channelVariant = masterVariant?.channels?.find(
    (channel) => String(channel._id) === String(listingVariantId),
  );

  if (!masterVariant) return null;

  const variantTitle = channelVariant?.title || masterVariant?.title || listingVariantId;

  return {
    variantId: listingVariantId,
    productId: listingId,
    title: buildProductVariantLinkTitle(productTitle, variantTitle),
    type: 'listingVariant',
    masterProductId: masterProduct._id,
    masterVariantId: masterVariant._id,
    ...channelFields,
    ...(isDraft ? { isDraft: true } : {}),
  };
};

export const buildListingAttachStoreLinksFromResponse = ({
  listing,
  masterProduct,
  masterProductId,
  updatedListing,
  updatedVariants = [],
  isDraft = true,
  salesChannels = [],
}) => {
  const listingId = updatedListing?._id || listing.listingId;
  const channelId = listing.channelId;
  const resolvedMasterProductId = masterProductId || masterProduct?._id;
  const listingProduct = updatedListing || { _id: listingId };
  const channelFields = resolveLinkChannelFields({
    channelId,
    listingProduct,
    salesChannels,
  });

  const product = listing.listingGallery && listingId
    ? buildListingGalleryLink({
      listingId,
      listingProduct,
      masterProductId: resolvedMasterProductId,
      ...channelFields,
      isDraft,
    })
    : null;

  const requestedVariantIds = new Set((listing.listingVariantIds || []).map(String));
  const productVariants = (updatedVariants || [])
    .filter(({ _id }) => requestedVariantIds.has(String(_id)))
    .map((listingVariant) => {
      const masterVariant = getMasterVariant(masterProduct, listingVariant.masterVariantId);
      const variantTitle = listingVariant.title || masterVariant?.title || listingVariant._id;

      return {
        variantId: listingVariant._id,
        productId: listingId,
        title: buildProductVariantLinkTitle(listingProduct?.title || masterProduct?.title, variantTitle),
        type: 'listingVariant',
        masterProductId: resolvedMasterProductId,
        masterVariantId: listingVariant.masterVariantId,
        ...channelFields,
        ...(isDraft ? { isDraft: true } : {}),
      };
    })
    .filter((variant) => variant.masterVariantId);

  return { product, productVariants };
};

export const buildListingAttachStoreLinks = ({
  masterProduct,
  listing,
  productsState,
  isDraft = true,
  salesChannels = [],
}) => {
  const { listingId } = listing;
  let listingProduct = findListingEntity(productsState, {
    masterProductId: masterProduct?._id,
    channelId: listing.channelId,
    listingProductId: listingId,
  });

  if (!listingProduct) {
    const channel = (masterProduct?.channels || []).find(
      (entry) => matchesChannelId(entry, listing.channelId),
    );
    listingProduct = channel?.listingProduct;
  }

  const channelFields = resolveLinkChannelFields({
    channelId: listing.channelId,
    listingProduct,
    salesChannels,
  });

  const product = listing.listingGallery
    ? buildListingGalleryLink({
      listingId,
      listingProduct,
      masterProductId: masterProduct?._id,
      ...channelFields,
      isDraft,
    })
    : null;

  const productVariants = (listing.listingVariantIds || [])
    .map((listingVariantId) => buildListingVariantLink({
      masterProduct,
      productsState,
      channelId: listing.channelId,
      listingId,
      listingVariantId,
      isDraft,
      salesChannels,
      listingProduct,
    }))
    .filter(Boolean);

  return { product, productVariants };
};
